import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { applyPerfClass } from "./lib/perfDetect";
import Navbar from "./components/Navbar";
import Hero, { HeroSocials } from "./components/Hero";
import HomeHero from "./components/HomeHero";
import NewsTicker from "./components/NewsTicker";
import HomeSlider from "./components/HomeSlider";
import SceltiStructures from "./components/home/SceltiStructures";
import HostlandBanner from "./components/home/HostlandBanner";
import HomeGuideLinks from "./components/home/HomeGuideLinks";
import HOME_FAQ from "./data/homeFaq";
import HomeMain, { Percorsi } from "./components/home/HomeMain";
import HomeAside from "./components/home/HomeAside";
// Elfsight widgets are heavy (external 250KB+ scripts). Lazy them so the
// initial mobile bundle stays small and the LCP/TBT scores improve.
const ElfsightReviews = lazy(() => import("./components/ElfsightReviews"));
const ElfsightHostLabReviews = lazy(() => import("./components/ElfsightHostLabReviews"));
import { LEGACY_REDIRECTS } from "./data/legacyRedirects";
import Footer from "./components/Footer";
import QuizModal from "./components/QuizModal";
import PodcastTicker from "./components/PodcastTicker";
const SubscribeLiveToasts = lazy(() => import("./components/SubscribeLiveToasts"));
const ScratchGamePopup = lazy(() => import("./components/ScratchGamePopup"));
const FaqBnbPopup = lazy(() => import("./components/FaqBnbPopup"));

// === CODE-SPLIT ROUTES ===
// All page-level routes are loaded on-demand via React.lazy() to keep the
// initial mobile JS bundle small. Homepage components above are kept eager
// because they render synchronously in <Landing /> below.
const ConsulenzePage = lazy(() => import("./pages/ConsulenzePage"));
const GuideAffittiBreviPage = lazy(() => import("./pages/GuideAffittiBreviPage"));
const GuideAffittiSuccessPage = lazy(() => import("./pages/GuideAffittiSuccessPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingConfirmPage = lazy(() => import("./pages/BookingConfirmPage"));
const EventiPage = lazy(() => import("./pages/EventiPage"));
const AutoreSergioPage = lazy(() => import("./pages/AutoreSergioPage"));
const HostLabMainPage = lazy(() => import("./pages/HostLabPage"));
const LeggeHostPage = lazy(() => import("./pages/LeggeHostPage"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage"));
const RadarPage = lazy(() => import("./pages/RadarPage"));
const HostLabTeacherPage = lazy(() => import("./pages/HostLabTeacherPage"));
const HostLabEventPage = lazy(() => import("./pages/HostLabEventPage"));
const HostLabVideoPage = lazy(() => import("./pages/HostLabVideoPage"));
const HostLabEbookPage = lazy(() => import("./pages/HostLabEbookPage"));
const HostLabWatchVideoPage = lazy(() => import("./pages/HostLabWatchVideoPage"));
const HostLabWatchReplayPage = lazy(() => import("./pages/HostLabWatchReplayPage"));
const HostLabSuccessPage = lazy(() => import("./pages/HostLabSuccessPage"));
const ServiziPage = lazy(() => import("./pages/ServiziPage"));
const HostNewsPage = lazy(() => import("./pages/HostNewsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const HostclubLandingPage = lazy(() => import("./pages/HostclubLandingPage"));
const HostclubDashboardPage = lazy(() => import("./pages/HostclubDashboardPage"));
const HostclubHostScanPage = lazy(() => import("./pages/HostclubHostScanPage"));
const NegozioPage = lazy(() => import("./pages/NegozioPage"));
const CartelliPage = lazy(() => import("./pages/CartelliPage"));
const OfferteAmazonPage = lazy(() => import("./pages/OfferteAmazonPage"));
const OffertePage = lazy(() => import("./pages/OffertePage"));
const NegozioGraziePage = lazy(() => import("./pages/NegozioPage").then((m) => ({ default: m.NegozioGraziePage })));
const PrintfulShopPage = lazy(() => import("./pages/PrintfulShopPage"));
const PrintfulProductPage = lazy(() => import("./pages/PrintfulProductPage"));
const PrintfulThankYouPage = lazy(() => import("./pages/PrintfulThankYouPage"));
const AppPage = lazy(() => import("./pages/AppPage"));
const LtPage = lazy(() => import("./pages/LtPage"));
const EcosistemaPage = lazy(() => import("./pages/EcosistemaPage"));
const FaqBdsrPage = lazy(() => import("./pages/FaqBdsrPage"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));
const AffittiBreviHubPage = lazy(() => import("./pages/AffittiBreviHubPage"));
const PrivacyPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const CookiePolicyPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.CookiePolicyPage })));
const TerminiVenditaPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.TerminiVenditaPage })));
const RecessoPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.RecessoPage })));
const SupportarciPage = lazy(() => import("./pages/SupportarciPage"));
const GrazieSostegnoPage = lazy(() => import("./pages/BlogPage").then((m) => ({ default: m.GrazieSostegnoPage })));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPage").then((m) => ({ default: m.BlogPostPage })));
const ContattiPage = lazy(() => import("./pages/ContattiPage"));
const ContattiSegnalazionePage = lazy(() => import("./pages/ContattiSegnalazionePage"));
const ContattiCollaboraPage = lazy(() => import("./pages/ContattiCollaboraPage"));
const PodcastPage = lazy(() => import("./pages/PodcastPage"));
const CodiciScontoPage = lazy(() => import("./pages/ServiziSubPages").then((m) => ({ default: m.CodiciScontoPage })));
const ViveriDiTurismo2026Page = lazy(() => import("./pages/ViveriDiTurismo2026Page"));
const CINPage = lazy(() => import("./pages/ServiziSubPages").then((m) => ({ default: m.CINPage })));
const SelfCheckinPage = lazy(() => import("./pages/ServiziSubPages").then((m) => ({ default: m.SelfCheckinPage })));
const SatispayPage = lazy(() => import("./pages/SatispayPage"));
const SumUpPage = lazy(() => import("./pages/SumUpPage"));
const SailyPage = lazy(() => import("./pages/SailyPage"));
const CantiereHostPage = lazy(() => import("./pages/CantiereHostPage"));
const ChekinPage = lazy(() => import("./pages/ChekinPage"));
const GetYourGuidePage = lazy(() => import("./pages/ServiziSubPages").then((m) => ({ default: m.GetYourGuidePage })));
const HostLabPage = lazy(() => import("./pages/ServiziSubPages").then((m) => ({ default: m.HostLabPage })));
const WiisyPage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.WiisyPage })));
const UberPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.UberPage })));
const LimePage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.LimePage })));
const TrainPalPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.TrainPalPage })));
const TheForkPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.TheForkPage })));
const SurfsharkPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.SurfsharkPage })));
const HomeExchangePage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.HomeExchangePage })));
const EmergentPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.EmergentPage })));
const AosomPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.AosomPage })));
const EonPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.EonPage })));
const TooGoodToGoPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.TooGoodToGoPage })));
const BuddyChatPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.BuddyChatPage })));
const EasyBnBPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.EasyBnBPage })));
const PriceLabsPage = lazy(() => import("./pages/TravelPartnerPages").then((m) => ({ default: m.PriceLabsPage })));
const HostRevenuePage = lazy(() => import("./pages/HostRevenuePage"));
const HostRevenueSuccessPage = lazy(() => import("./pages/HostRevenueSuccessPage"));
const HostRevenueReportPage = lazy(() => import("./pages/HostRevenueReportPage"));
const AervirdisPage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.AervirdisPage })));
const CheckInScanPage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.CheckInScanPage })));
const DroidAccessPage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.DroidAccessPage })));
const HoliduPage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.HoliduPage })));
const RaiCanonePage = lazy(() => import("./pages/ServiziPartnerPages").then((m) => ({ default: m.RaiCanonePage })));
const CinFacilePage = lazy(() => import("./pages/CinFacilePage"));
const HappyHourPage = lazy(() => import("./pages/HappyHourPage"));
const AvviaBnbPage = lazy(() => import("./pages/AvviaBnbPage"));
const IniziaAvventuraPage = lazy(() => import("./pages/IniziaAvventuraPage"));
const CheckInDautorePage = lazy(() => import("./pages/CheckInDautorePage"));
const StagingConAnimaPage = lazy(() => import("./pages/StagingConAnimaPage"));
const CorsiPage = lazy(() => import("./pages/CorsiPage"));
const DichiarazioneImpostaSoggiornoPage = lazy(() => import("./pages/DichiarazioneImpostaSoggiornoPage"));
const AppUfficialiPage = lazy(() => import("./pages/AppUfficialiPage"));
const DeVisuPage = lazy(() => import("./pages/DeVisuPage"));
const DeVisuDashboardPage = lazy(() => import("./pages/DeVisuDashboardPage"));
const DeVisuGuestPage = lazy(() => import("./pages/DeVisuGuestPage"));
const DeVisuHostCallPage = lazy(() => import("./pages/DeVisuHostCallPage"));
const DeVisuLoginPage = lazy(() => import("./pages/DeVisuAuthPages").then(m => ({ default: m.DeVisuLoginPage })));
const DeVisuRegisterPage = lazy(() => import("./pages/DeVisuAuthPages").then(m => ({ default: m.DeVisuRegisterPage })));
const AnalizzaAnnuncioPage = lazy(() => import("./pages/AnalizzaAnnuncioPage"));
const AntiScamPage = lazy(() => import("./pages/AntiScamPage"));
const BagniPubbliciPage = lazy(() => import("./pages/BagniPubbliciPage"));
const BagniViewerPage = lazy(() => import("./pages/BagniViewerPage"));
const HostPridePage = lazy(() => import("./pages/HostPridePage"));
const PhotoBoostPage = lazy(() => import("./pages/PhotoBoostPage"));
import BreakingNewsBar from "./components/BreakingNewsBar";
const BigliettiPage = lazy(() => import("./pages/NegozioProdottiPages").then((m) => ({ default: m.BigliettiPage })));
const MagiKeyPage = lazy(() => import("./pages/NegozioProdottiPages").then((m) => ({ default: m.MagiKeyPage })));
const TargheCinPage = lazy(() => import("./pages/NegozioProdottiPages").then((m) => ({ default: m.TargheCinPage })));
const PortachiaviIroniciPage = lazy(() => import("./pages/NegozioProdottiPages").then((m) => ({ default: m.PortachiaviIroniciPage })));
const ZerbiniDivertentiPage = lazy(() => import("./pages/NegozioProdottiPages").then((m) => ({ default: m.ZerbiniDivertentiPage })));
import CookieBanner from "./components/CookieBanner";
import PWAInstallBanner from "./components/PWAInstallBanner";
import IOSInstallBanner from "./components/IOSInstallBanner";
import OneSignalInit from "./components/OneSignalInit";
import ServiceWorkerRegister from "./components/ServiceWorkerRegister";
import ScrollToTop from "./components/ScrollToTop";
import BackToHome from "./components/BackToHome";
import useSEO from "./hooks/useSEO";
const OfflinePage = lazy(() => import("./pages/OfflinePage"));
const PWATestPage = lazy(() => import("./pages/PWATestPage"));
const HostclubSubscribePage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubSubscribePage })));
const HostclubSuccessPage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubSuccessPage })));
const HostclubVerifyPage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubVerifyPage })));
const HostclubLoginPage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubLoginPage })));
const HostclubForgotPasswordPage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubForgotPasswordPage })));
const HostclubResetPasswordPage = lazy(() => import("./pages/HostclubAuthPages").then((m) => ({ default: m.HostclubResetPasswordPage })));
const HostclubProfilePage = lazy(() => import("./pages/HostclubCommunityPages").then((m) => ({ default: m.HostclubProfilePage })));
const HostclubForumPage = lazy(() => import("./pages/HostclubCommunityPages").then((m) => ({ default: m.HostclubForumPage })));
const HostclubTopicPage = lazy(() => import("./pages/HostclubCommunityPages").then((m) => ({ default: m.HostclubTopicPage })));
const DirettePage = lazy(() => import("./pages/DirettePage").then((m) => ({ default: m.DirettePage })));
const DiretteDetailPage = lazy(() => import("./pages/DirettePage").then((m) => ({ default: m.DiretteDetailPage })));

// 301-style client redirect for legacy /dirette/:id → /hostshow/:id
function LegacyDiretteRedirect() {
  const { id } = useParams();
  return <Navigate to={`/hostshow/${id}`} replace />;
}
const QrGeneratorPage = lazy(() => import("./pages/QrGeneratorPage"));
const BookingReviewsGuidePage = lazy(() => import("./pages/BookingReviewsGuidePage"));
const ToolRecensioniBookingPage = lazy(() => import("./pages/ToolRecensioniBookingPage"));
const CalcolatoreGuadagniPage = lazy(() => import("./pages/CalcolatoreGuadagniPage"));
const CalcoloImpostaSoggiornoPage = lazy(() => import("./pages/CalcoloImpostaSoggiornoPage"));
const HostmarketPage = lazy(() => import("./pages/HostmarketPage"));
const FaqBnbPage = lazy(() => import("./pages/FaqBnbPage"));
const NumeriUtiliPage = lazy(() => import("./pages/NumeriUtiliPage"));
const NegozioGuestbookPage = lazy(() => import("./pages/NegozioGuestbookPage"));
const ServiziGuestbookPage = lazy(() => import("./pages/ServiziGuestbookPage"));
const ServiziGuestbookDigitalePage = lazy(() => import("./pages/ServiziGuestbookDigitalePage"));
const WelcomebookSatellitePage = lazy(() => import("./pages/WelcomebookSatellitePage"));
const RegoleCasaAirbnbPage = lazy(() => import("./pages/RegoleCasaAirbnbPage"));
const QrCodeWifiOspitiPage = lazy(() => import("./pages/QrCodeWifiOspitiPage"));
import FaqBnbBanner from "./components/FaqBnbBanner";
import AppDownloadBanner from "./components/AppDownloadBanner";
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const ChiSiamoPage = lazy(() => import("./pages/ChiSiamoPage"));
const AlloggiatiWebPage = lazy(() => import("./pages/AlloggiatiWebPage"));
const AssociazioniPage = lazy(() => import("./pages/AssociazioniPage"));
const AssociazioneDetailPage = lazy(() => import("./pages/AssociazioneDetailPage"));
const AssociazioniRegionePage = lazy(() => import("./pages/AssociazioniRegionePage"));
const AssociazioniComunePage = lazy(() => import("./pages/AssociazioniComunePage"));
const IlCorriereDellHostPage = lazy(() => import("./pages/IlCorriereDellHostPage"));
const SceltoDaVitaDaHostPage = lazy(() => import("./pages/SceltoDaVitaDaHostPage"));
const SceltoStructureDetailPage = lazy(() => import("./pages/SceltoStructureDetailPage"));


import { AuthProvider } from "./context/AuthContext";

// Silence benign ResizeObserver loop warning (triggered by 3rd party widgets like Elfsight)
// This warning does NOT affect functionality; only a dev-overlay noise from widget resize loops.
if (typeof window !== "undefined") {
  const RO_ERR_PATTERNS = [
    "ResizeObserver loop completed with undelivered notifications",
    "ResizeObserver loop limit exceeded",
  ];
  const isROErr = (msg) =>
    typeof msg === "string" && RO_ERR_PATTERNS.some((p) => msg.includes(p));

  const origError = window.console.error;
  window.console.error = (...args) => {
    if (args[0] && isROErr(args[0])) return;
    origError(...args);
  };

  // Capture phase to win over CRA's error-overlay listener
  window.addEventListener(
    "error",
    (e) => {
      if (e && (isROErr(e.message) || (e.error && isROErr(e.error.message)))) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
  window.addEventListener(
    "unhandledrejection",
    (e) => {
      if (e && e.reason && isROErr(e.reason.message || e.reason)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );

  // Patch ResizeObserver so it swallows the specific loop error before CRA sees it
  if (window.ResizeObserver) {
    const OrigRO = window.ResizeObserver;
    window.ResizeObserver = class PatchedRO extends OrigRO {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (e) {
              if (!isROErr(e && e.message)) throw e;
            }
          });
        });
      }
    };
  }
}

const Landing = () => {
  const [quizOpen, setQuizOpen] = useState(false);

  useSEO({
    title: "Vita da Host: portale per Host e PM di affitti brevi e B&B",
    description:
      "Normative, tasse, CIN e Alloggiati Web spiegati da host per host. Consulenze gratuite, corsi, strumenti e Hostland per B&B, case vacanza e affitti brevi.",
    image: "https://www.vitadahost.it/og/vdh-home.jpg",
    imageAlt:
      "Vita da Host, il portale per host di B&B, case vacanza e locazioni turistiche.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://www.vitadahost.it/#website",
          "url": "https://www.vitadahost.it/",
          "name": "Vita da Host",
          "alternateName": ["vitadahost.it", "VitadaHost"],
          "inLanguage": "it-IT",
          "publisher": { "@id": "https://www.vitadahost.it/#organization" },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://www.vitadahost.it/blog?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "Organization",
          "@id": "https://www.vitadahost.it/#organization",
          "name": "Vita da Host",
          "url": "https://www.vitadahost.it/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.vitadahost.it/icon-512.png",
            "width": 512,
            "height": 512,
          },
          "description":
            "Portale italiano per host di B&B, case vacanza, affittacamere, agriturismi, glamping e locazioni turistiche: notizie, guide, consulenze, corsi e strumenti.",
          "foundingDate": "2023",
          "founder": { "@type": "Person", "name": "Sergio Battelli" },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Genova",
            "addressRegion": "Liguria",
            "addressCountry": "IT",
          },
          "vatID": "IT02903680995",
          "email": "vitadahost@gmail.com",
          "knowsAbout": [
            "affitti brevi",
            "B&B",
            "case vacanza",
            "affittacamere",
            "agriturismi",
            "glamping",
            "locazioni turistiche",
            "normativa extralberghiera",
            "fiscalità affitti brevi",
          ],
          "sameAs": [
            "https://www.instagram.com/vita_da_host_/",
            "https://www.facebook.com/VitadaHostUfficiale/",
            "https://www.threads.com/@vita_da_host_",
            "https://www.tiktok.com/@vita.da.host",
            "https://www.linkedin.com/in/sergio-battelli-vitadahost/",
            "https://youtube.com/@vitadahostchannel",
            "https://apps.apple.com/it/app/vita-da-host/id6760694341",
            "https://play.google.com/store/apps/details?id=com.wnapp.id1778937032200",
          ],
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.vitadahost.it/#faq",
          "mainEntity": HOME_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  });

  return (
    <div
      className="min-h-screen bg-[#F4F5F7] text-[#111318] relative"
    >
      <Navbar />
      {/* HOSTNEWS ticker — ancorato sotto l'header, sempre in vista */}
      <NewsTicker />
      <main className="relative z-10">
        {/* HERO (light, mockup) */}
        <HomeHero />

        {/* SCELTO DA VITA DA HOST — subito sotto la hero (stessa distanza dello slider) */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-3 md:pt-4">
          <SceltiStructures />
        </div>

        {/* DA DOVE VUOI PARTIRE? — in mezzo tra Scelto e il banner slider */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-8 md:pt-10">
          <Percorsi />
        </div>

        {/* BANNER HOSTLAND — subito sotto le 3 colonne, prima del blocco Pubblicità */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-6 md:pt-8">
          <HostlandBanner />
        </div>

        {/* BANNER SLIDER 1200×300 — tra "Da dove vuoi partire" e le ultime novità */}
        <HomeSlider />

        {/* 2-column body — mockup flex layout (main 999/640, aside 1/320, wraps < lg) */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-12 md:pt-14 pb-16">
          <div className="flex flex-wrap gap-10 items-start">
            <HomeMain
              reviewsGoogle={
                <Suspense fallback={<div className="h-32" aria-hidden />}>
                  <ElfsightReviews />
                </Suspense>
              }
              reviewsHostLab={
                <Suspense fallback={<div className="h-32" aria-hidden />}>
                  <ElfsightHostLabReviews />
                </Suspense>
              }
            />
            <HomeAside />
          </div>
        </div>

        {/* LE GUIDE PIÙ CERCATE — in fondo alla home, sotto il blog */}
        <HomeGuideLinks />
      </main>
      <Footer />
      <QuizModal open={quizOpen} onClose={() => setQuizOpen(false)} />
    </div>
  );
};

function App() {
  // Auto-detect low-end devices and apply CSS class for graceful degradation
  useEffect(() => {
    applyPerfClass();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen bg-black" aria-hidden />}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/benvenuto" element={<Navigate to="/" replace />} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          {/* ─── Legacy WordPress URLs (migrazione vecchio sito vitadahost.it) ─── */}
          {/* Auto-generated 301 redirects from legacyRedirects.js (308 entries). */}
          {LEGACY_REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}

          {/* Legacy WordPress URLs (vecchio sito vitadahost.it) → /servizi */}
          <Route path="/i-partner-di-vita-da-host.html" element={<Navigate to="/servizi" replace />} />
          <Route path="/i-partner-di-vita-da-host" element={<Navigate to="/servizi" replace />} />
          <Route path="/partner.html" element={<Navigate to="/servizi" replace />} />
          <Route path="/partner" element={<Navigate to="/servizi" replace />} />
          <Route path="/i-partner" element={<Navigate to="/servizi" replace />} />
          <Route path="/servizi.html" element={<Navigate to="/servizi" replace />} />
          <Route path="/consulenze" element={<ConsulenzePage />} />
          <Route path="/guide-affitti-brevi" element={<GuideAffittiBreviPage />} />
          <Route path="/guide-affitti-brevi/success" element={<GuideAffittiSuccessPage />} />
          <Route path="/GuideAffittiBrevi" element={<Navigate to="/guide-affitti-brevi" replace />} />
          <Route path="/consulenze/prenota/:slug" element={<BookingPage />} />
          <Route path="/consulenze/prenota/:slug/confermata" element={<BookingConfirmPage />} />
          <Route path="/hostclub/consulenze/:slug" element={<BookingPage hostclub />} />
          <Route path="/eventi" element={<EventiPage />} />
          <Route path="/eventi/:slug" element={<EventiPage />} />
          <Route path="/hostlab" element={<HostLabMainPage />} />
          <Route path="/leggehost" element={<LeggeHostPage />} />
          <Route path="/LeggeHost" element={<Navigate to="/leggehost" replace />} />
          <Route path="/lanostralegge" element={<Navigate to="/leggehost" replace />} />
          <Route path="/hostlab/insegnante/:slug" element={<HostLabTeacherPage />} />
          <Route path="/hostlab/lezione/:id" element={<HostLabEventPage />} />
          <Route path="/hostlab/video/:id" element={<HostLabVideoPage />} />
          <Route path="/hostlab/ebook/:id" element={<HostLabEbookPage />} />
          <Route path="/hostlab/video/:id/riproduci" element={<HostLabWatchVideoPage />} />
          <Route path="/hostlab/replay/:id/riproduci" element={<HostLabWatchReplayPage />} />
          <Route path="/hostlab/success" element={<HostLabSuccessPage />} />
          <Route path="/servizi" element={<ServiziPage />} />
          <Route path="/hostnews" element={<HostNewsPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/radar" element={<RadarPage />} />
          <Route path="/servizi/avviabnb" element={<AvviaBnbPage />} />
          <Route path="/servizi/inizia-avventura" element={<IniziaAvventuraPage />} />
          <Route path="/servizi/sono-host-me-ne-vanto" element={<HostPridePage />} />
          <Route path="/servizi/photoboost" element={<PhotoBoostPage />} />
          <Route path="/hostpride" element={<Navigate to="/servizi/sono-host-me-ne-vanto" replace />} />
          <Route path="/photoboost" element={<Navigate to="/servizi/photoboost" replace />} />
          <Route path="/servizi/check-in-dautore" element={<CheckInDautorePage />} />
          <Route path="/servizi/staging-con-anima" element={<StagingConAnimaPage />} />
          <Route path="/staging-con-anima" element={<Navigate to="/servizi/staging-con-anima" replace />} />
          <Route path="/servizi/homestaging-extralberghiero" element={<Navigate to="/servizi/staging-con-anima" replace />} />
          <Route path="/servizi/specializzazione-extralberghiera" element={<Navigate to="/servizi/staging-con-anima" replace />} />
          <Route path="/corsi" element={<CorsiPage />} />
          <Route path="/cantierehost" element={<CantiereHostPage />} />
          <Route path="/i-corsi" element={<Navigate to="/corsi" replace />} />
          <Route path="/check-in-dautore" element={<Navigate to="/servizi/check-in-dautore" replace />} />
          <Route path="/servizi/homestaging" element={<Navigate to="/servizi/check-in-dautore" replace />} />
          <Route path="/inizia-avventura" element={<Navigate to="/servizi/inizia-avventura" replace />} />
          <Route path="/aprire-bnb" element={<Navigate to="/servizi/inizia-avventura" replace />} />
          <Route path="/aprire-bnb-2026" element={<Navigate to="/servizi/inizia-avventura" replace />} />
          <Route path="/servizi/cin-facile" element={<CinFacilePage />} />
          {/* CODICI SCONTO — moved from /servizi/* to /negozio/* for SEO (Feb 2026).
              The new canonical URLs live under /negozio/codici-sconto. The old
              /servizi/codici-sconto* paths redirect (client-side) to preserve
              every inbound link & previously indexed page. */}
          <Route path="/negozio/codici-sconto" element={<CodiciScontoPage />} />
          <Route path="/happyhour" element={<HappyHourPage />} />
          <Route path="/happy-hour" element={<Navigate to="/happyhour" replace />} />
          <Route path="/negozio/codici-sconto/wiisy" element={<WiisyPage />} />
          <Route path="/negozio/codici-sconto/uber" element={<UberPage />} />
          <Route path="/negozio/codici-sconto/lime" element={<LimePage />} />
          <Route path="/negozio/codici-sconto/trainpal" element={<TrainPalPage />} />
          <Route path="/negozio/codici-sconto/thefork" element={<TheForkPage />} />
          <Route path="/negozio/codici-sconto/surfshark" element={<SurfsharkPage />} />
          <Route path="/negozio/codici-sconto/homeexchange" element={<HomeExchangePage />} />
          <Route path="/negozio/codici-sconto/emergent" element={<EmergentPage />} />
          <Route path="/negozio/codici-sconto/aosom" element={<AosomPage />} />
          <Route path="/negozio/codici-sconto/eon" element={<EonPage />} />
          <Route path="/negozio/codici-sconto/too-good-to-go" element={<TooGoodToGoPage />} />
          <Route path="/negozio/codici-sconto/aervirdis" element={<AervirdisPage />} />
          <Route path="/negozio/codici-sconto/check-in-scan" element={<CheckInScanPage />} />
          <Route path="/negozio/codici-sconto/droid-access" element={<DroidAccessPage />} />
          <Route path="/negozio/codici-sconto/holidu" element={<HoliduPage />} />
          <Route path="/negozio/codici-sconto/buddychat" element={<BuddyChatPage />} />
          <Route path="/negozio/codici-sconto/easybnb" element={<EasyBnBPage />} />
          <Route path="/negozio/codici-sconto/pricelabs" element={<PriceLabsPage />} />
          <Route path="/negozio/codici-sconto/saily" element={<SailyPage />} />
          <Route path="/servizi/hostrevenue" element={<HostRevenuePage />} />
          <Route path="/servizi/hostrevenue/success" element={<HostRevenueSuccessPage />} />
          <Route path="/servizi/hostrevenue/report/:orderId" element={<HostRevenueReportPage />} />
          <Route path="/servizi/hostrevenue/demo" element={<HostRevenueReportPage />} />
          <Route path="/servizi/hostrevenue/cancel" element={<Navigate to="/servizi/hostrevenue?cancelled=1" replace />} />
          <Route path="/hostrevenue" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/host-revenue" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/market-report" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/report-mercato-affitti-brevi" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/analisi-mercato-airbnb" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/airroi-report" element={<Navigate to="/servizi/hostrevenue" replace />} />
          <Route path="/servizi/codici-sconto/pricelabs" element={<Navigate to="/negozio/codici-sconto/pricelabs" replace />} />
          <Route path="/pricelabs" element={<Navigate to="/negozio/codici-sconto/pricelabs" replace />} />
          <Route path="/sconto-pricelabs" element={<Navigate to="/negozio/codici-sconto/pricelabs" replace />} />
          <Route path="/prezzo-dinamico" element={<Navigate to="/negozio/codici-sconto/pricelabs" replace />} />
          <Route path="/dynamic-pricing" element={<Navigate to="/negozio/codici-sconto/pricelabs" replace />} />
          <Route path="/negozio/codici-sconto/vivere-di-turismo-2026" element={<ViveriDiTurismo2026Page />} />
          <Route path="/sconto-vivere-di-turismo-2026" element={<Navigate to="/negozio/codici-sconto/vivere-di-turismo-2026" replace />} />
          <Route path="/coupon-vivere-di-turismo-2026" element={<Navigate to="/negozio/codici-sconto/vivere-di-turismo-2026" replace />} />
          <Route path="/servizi/codici-sconto" element={<Navigate to="/negozio/codici-sconto" replace />} />
          <Route path="/servizi/codici-sconto/wiisy" element={<Navigate to="/negozio/codici-sconto/wiisy" replace />} />
          <Route path="/servizi/codici-sconto/aervirdis" element={<Navigate to="/negozio/codici-sconto/aervirdis" replace />} />
          <Route path="/servizi/codici-sconto/check-in-scan" element={<Navigate to="/negozio/codici-sconto/check-in-scan" replace />} />
          <Route path="/servizi/codici-sconto/droid-access" element={<Navigate to="/negozio/codici-sconto/droid-access" replace />} />
          <Route path="/servizi/codici-sconto/holidu" element={<Navigate to="/negozio/codici-sconto/holidu" replace />} />
          <Route path="/servizi/cin" element={<CINPage />} />
          <Route path="/servizi/alloggiati-web" element={<AlloggiatiWebPage />} />
          <Route path="/servizi/alloggiati" element={<Navigate to="/servizi/alloggiati-web" replace />} />
          <Route path="/servizi/schedine-alloggiati" element={<Navigate to="/servizi/alloggiati-web" replace />} />
          <Route path="/servizi/associazioni" element={<AssociazioniPage />} />
          <Route path="/servizi/scelto-da-vita-da-host" element={<SceltoDaVitaDaHostPage />} />
          <Route path="/scelto-da-vita-da-host" element={<Navigate to="/servizi/scelto-da-vita-da-host" replace />} />
          <Route path="/scelto/:id" element={<SceltoStructureDetailPage />} />
          <Route path="/scelto" element={<Navigate to="/servizi/scelto-da-vita-da-host" replace />} />
          <Route path="/servizi/associazioni/regione/:slug" element={<AssociazioniRegionePage />} />
          <Route path="/servizi/associazioni/comune/:slug" element={<AssociazioniComunePage />} />
          <Route path="/servizi/associazioni/:slug" element={<AssociazioneDetailPage />} />
          <Route path="/associazioni" element={<Navigate to="/servizi/associazioni" replace />} />
          <Route path="/servizi/dichiarazione-imposta-soggiorno" element={<DichiarazioneImpostaSoggiornoPage />} />
          <Route path="/servizi/imposta-di-soggiorno" element={<Navigate to="/servizi/dichiarazione-imposta-soggiorno" replace />} />
          <Route path="/servizi/app-ufficiali" element={<AppUfficialiPage />} />
          <Route path="/devisu" element={<DeVisuPage />} />
          <Route path="/devisu/login" element={<DeVisuLoginPage />} />
          <Route path="/devisu/register" element={<DeVisuRegisterPage />} />
          <Route path="/devisu/dashboard" element={<DeVisuDashboardPage />} />
          <Route path="/devisu/g/:accessToken" element={<DeVisuGuestPage />} />
          <Route path="/devisu/host/:sessionId" element={<DeVisuHostCallPage />} />
          <Route path="/servizi/devisu" element={<Navigate to="/devisu" replace />} />
          <Route path="/negozio/servizi/devisu" element={<Navigate to="/devisu" replace />} />
          <Route path="/servizi/hostrun" element={<Navigate to="/" replace />} />
          <Route path="/servizi/host-run" element={<Navigate to="/" replace />} />
          <Route path="/servizi/scarica-app" element={<Navigate to="/servizi/app-ufficiali" replace />} />
          <Route path="/servizi/self-checkin" element={<SelfCheckinPage />} />
          <Route path="/servizi/satispay-per-host" element={<SatispayPage />} />
          <Route path="/satispay-per-host" element={<Navigate to="/servizi/satispay-per-host" replace />} />
          <Route path="/negozio/codici-sconto/satispay" element={<Navigate to="/servizi/satispay-per-host" replace />} />

          <Route path="/servizi/sumup-per-host" element={<SumUpPage />} />
          <Route path="/sumup-per-host" element={<Navigate to="/servizi/sumup-per-host" replace />} />
          <Route path="/negozio/codici-sconto/sumup" element={<Navigate to="/servizi/sumup-per-host" replace />} />

          <Route path="/servizi/chekin" element={<ChekinPage />} />
          <Route path="/chekin" element={<Navigate to="/servizi/chekin" replace />} />
          <Route path="/negozio/codici-sconto/chekin" element={<Navigate to="/servizi/chekin" replace />} />
          <Route path="/servizi/getyourguide" element={<GetYourGuidePage />} />
          {/* /servizi/hostlab → NEW /hostlab (Fase 2 rebuild: piattaforma live+video full) */}
          <Route path="/servizi/hostlab" element={<Navigate to="/hostlab" replace />} />
          <Route path="/servizi/rai-canone" element={<RaiCanonePage />} />
          <Route path="/podcast" element={<PodcastPage />} />
          <Route path="/servizi/podcast" element={<Navigate to="/podcast" replace />} />
          <Route path="/hostshow" element={<DirettePage />} />
          <Route path="/hostshow/:id" element={<DiretteDetailPage />} />
          {/* Legacy /dirette routes — kept as redirects to preserve inbound links + Google index equity */}
          <Route path="/dirette" element={<Navigate to="/hostshow" replace />} />
          <Route path="/dirette/:id" element={<LegacyDiretteRedirect />} />
          <Route path="/qrcodegratis" element={<QrGeneratorPage />} />
          {/* Legacy: keep /tools/qrcode working as SEO 301-like redirect to new indexed URL */}
          <Route path="/tools/qrcode" element={<Navigate to="/qrcodegratis" replace />} />
          {/* Guest-facing multilingual review guide (linked via printed QR) */}
          <Route path="/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/en/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/es/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/fr/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/de/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/zh/recensioni-booking" element={<BookingReviewsGuidePage />} />
          <Route path="/hi/recensioni-booking" element={<BookingReviewsGuidePage />} />
          {/* Host-facing tool page (presents the tool + QR generator) */}
          <Route path="/servizi/tool-recensioni-booking" element={<ToolRecensioniBookingPage />} />
          <Route path="/tools/analizza-annuncio" element={<AnalizzaAnnuncioPage />} />
          <Route path="/antiscam" element={<AntiScamPage />} />
          <Route path="/tools/antiscam" element={<Navigate to="/antiscam" replace />} />
          <Route path="/tools/anti-scam" element={<Navigate to="/antiscam" replace />} />
          <Route path="/tools/bagni-pubblici" element={<BagniPubbliciPage />} />
          <Route path="/tools/bagni-pubblici/:city" element={<BagniPubbliciPage />} />
          <Route path="/bagni/:city" element={<BagniViewerPage />} />
          <Route path="/calcolatoreguadagniaffittibrevi" element={<CalcolatoreGuadagniPage />} />
          {/* Legacy: keep old URLs working as SEO 301-like redirects to new indexed URL */}
          <Route path="/strumenti/calcolatore-guadagni" element={<Navigate to="/calcolatoreguadagniaffittibrevi" replace />} />
          <Route path="/calcolatore-guadagni" element={<Navigate to="/calcolatoreguadagniaffittibrevi" replace />} />
          <Route path="/strumenti/calcolo-imposta-soggiorno" element={<CalcoloImpostaSoggiornoPage />} />
          <Route path="/strumenti/calcolo-notti-imposta-soggiorno" element={<Navigate to="/strumenti/calcolo-imposta-soggiorno" replace />} />
          <Route path="/strumenti/tassa-soggiorno" element={<Navigate to="/strumenti/calcolo-imposta-soggiorno" replace />} />
          <Route path="/servizi/hostmarket" element={<HostmarketPage />} />
          <Route path="/hostmarket" element={<Navigate to="/servizi/hostmarket" replace />} />
          <Route path="/servizi/faqbnb" element={<FaqBnbPage />} />
          <Route path="/faqbnb" element={<Navigate to="/servizi/faqbnb" replace />} />
          <Route path="/ebook/faqbnb" element={<Navigate to="/servizi/faqbnb" replace />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/chi-siamo" element={<ChiSiamoPage />} />
          <Route path="/about" element={<Navigate to="/chi-siamo" replace />} />
          <Route path="/contatti" element={<ContattiPage />} />
          <Route path="/contatti/segnalazione" element={<ContattiSegnalazionePage />} />
          <Route path="/contatti/collabora" element={<ContattiCollaboraPage />} />
          <Route path="/contatti/lavora-con-noi" element={<Navigate to="/contatti/collabora" replace />} />
          <Route path="/contatti/pubblicita" element={<Navigate to="/contatti/collabora" replace />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/hostclub" element={<HostclubLandingPage />} />
          <Route path="/hostclub/subscribe" element={<HostclubSubscribePage />} />
          <Route path="/hostclub/login" element={<HostclubLoginPage />} />
          <Route path="/hostclub/success" element={<HostclubSuccessPage />} />
          <Route path="/hostclub/verify/:token" element={<HostclubVerifyPage />} />
          <Route path="/hostclub/forgot-password" element={<HostclubForgotPasswordPage />} />
          <Route path="/hostclub/reset-password/:token" element={<HostclubResetPasswordPage />} />
          <Route path="/hostclub/dashboard" element={<HostclubDashboardPage />} />
          <Route path="/hostclub/hostscan" element={<HostclubHostScanPage />} />
          <Route path="/hostclub/profilo" element={<HostclubProfilePage />} />
          <Route path="/hostclub/community" element={<Navigate to="/hostclub/dashboard" replace />} />
          <Route path="/hostclub/forum" element={<HostclubForumPage />} />
          <Route path="/hostclub/forum/:id" element={<HostclubTopicPage />} />
          <Route path="/negozio" element={<NegozioPage />} />
          <Route path="/cartelli" element={<CartelliPage />} />
          <Route path="/negozio/offerte-amazon" element={<Navigate to="/offerte" replace />} />
          <Route path="/offerte" element={<OffertePage />} />
          <Route path="/negozio/printful" element={<PrintfulShopPage />} />
          <Route path="/negozio/printful/grazie" element={<PrintfulThankYouPage />} />
          <Route path="/negozio/printful/prodotto/:id" element={<PrintfulProductPage />} />
          <Route path="/negozio/biglietti-da-visita" element={<BigliettiPage />} />
          <Route path="/negozio/magikey" element={<MagiKeyPage />} />
          <Route path="/negozio/targhe-cin" element={<TargheCinPage />} />
          <Route path="/negozio/portachiavi-ironici" element={<PortachiaviIroniciPage />} />
          <Route path="/negozio/zerbini-divertenti" element={<ZerbiniDivertentiPage />} />
          <Route path="/negozio/guestbook-non-noioso" element={<NegozioGuestbookPage />} />
          <Route path="/servizi/guestbook-non-noioso" element={<ServiziGuestbookPage />} />
          <Route path="/guestbook-digitale" element={<ServiziGuestbookDigitalePage />} />
          <Route path="/servizi/guestbook-digitale" element={<Navigate to="/guestbook-digitale" replace />} />
          <Route path="/servizi/welcomebook-digitale" element={<Navigate to="/guestbook-digitale" replace />} />
          <Route path="/negozio/guestbook-digitale" element={<Navigate to="/guestbook-digitale" replace />} />
          {/* Cluster Welcomebook — pagine satellite per struttura */}
          <Route path="/welcome-book-hotel" element={<WelcomebookSatellitePage slug="welcome-book-hotel" />} />
          <Route path="/welcome-book-campeggio" element={<WelcomebookSatellitePage slug="welcome-book-campeggio" />} />
          <Route path="/welcome-book-bb" element={<WelcomebookSatellitePage slug="welcome-book-bb" />} />
          <Route path="/welcome-book-affittacamere" element={<WelcomebookSatellitePage slug="welcome-book-affittacamere" />} />
          <Route path="/welcome-book-case-vacanze" element={<WelcomebookSatellitePage slug="welcome-book-case-vacanze" />} />
          <Route path="/welcome-book-property-manager" element={<WelcomebookSatellitePage slug="welcome-book-property-manager" />} />
          {/* Cluster Welcomebook — generatori gratuiti */}
          <Route path="/regole-della-casa-airbnb" element={<RegoleCasaAirbnbPage />} />
          <Route path="/qr-code-wifi-ospiti" element={<QrCodeWifiOspitiPage />} />
          <Route path="/servizi/numeri-utili" element={<NumeriUtiliPage />} />
          <Route path="/servizi/il-corriere-dell-host" element={<IlCorriereDellHostPage />} />
          <Route path="/corriere" element={<Navigate to="/servizi/il-corriere-dell-host" replace />} />
          <Route path="/servizi/corriere" element={<Navigate to="/servizi/il-corriere-dell-host" replace />} />
          <Route path="/numeri-utili" element={<Navigate to="/servizi/numeri-utili" replace />} />
          <Route path="/guestbook-non-noioso" element={<Navigate to="/servizi/guestbook-non-noioso" replace />} />
          <Route path="/guestbook" element={<Navigate to="/servizi/guestbook-non-noioso" replace />} />
          <Route path="/negozio/grazie" element={<NegozioGraziePage />} />
          <Route path="/app" element={<AppPage />} />
          <Route path="/servizi/app" element={<AppPage />} />
          <Route path="/servizi/lt" element={<LtPage />} />
          <Route path="/servizi/locazione-turistica" element={<Navigate to="/servizi/lt" replace />} />
          {/* Hostland si è spostato sul sottodominio: redirect hard esterno (niente SPA, niente contenuto duplicato). */}
          <Route path="/servizi/hostland" element={<ExternalRedirect to="/hostland" />} />
          <Route path="/hostland" element={<ExternalRedirect to="/hostland/" />} />
          <Route path="/zerocommissioni" element={<ExternalRedirect to="/zerocommissioni/" />} />
          <Route path="/ecosistemavitadahost" element={<EcosistemaPage />} />
          <Route path="/servizi/faq-bdsr" element={<FaqBdsrPage />} />
          <Route path="/faq-bdsr" element={<Navigate to="/servizi/faq-bdsr" replace />} />
          <Route path="/faq-ministero" element={<Navigate to="/servizi/faq-bdsr" replace />} />
          <Route path="/faq-cin" element={<Navigate to="/servizi/faq-bdsr" replace />} />
          <Route path="/ecosistema-vita-da-host" element={<Navigate to="/ecosistemavitadahost" replace />} />
          <Route path="/ecosistema" element={<Navigate to="/ecosistemavitadahost" replace />} />
          <Route path="/le-nostre-app" element={<Navigate to="/ecosistemavitadahost" replace />} />
          <Route path="/affitti-brevi" element={<AffittiBreviHubPage />} />
          <Route path="/affitti-brevi/:slug" element={<CityLandingPage variantKey="affitti-brevi" />} />
          <Route path="/avvia-bnb/:slug" element={<CityLandingPage variantKey="avvia-bnb" />} />
          <Route path="/apertura-case-vacanze/:slug" element={<CityLandingPage variantKey="apertura-case-vacanze" />} />
          <Route path="/consulenze/:slug" element={<CityLandingPage variantKey="consulenze" />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/termini-vendita" element={<TerminiVenditaPage />} />
          <Route path="/recesso" element={<RecessoPage />} />
          <Route path="/resi" element={<Navigate to="/recesso" replace />} />
          <Route path="/condizioni-vendita" element={<Navigate to="/termini-vendita" replace />} />
          <Route path="/supportarci" element={<SupportarciPage />} />
          <Route path="/seguici" element={<SupportarciPage />} />
          <Route path="/grazie-sostegno" element={<GrazieSostegnoPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/autore/sergio-battelli" element={<AutoreSergioPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/offline" element={<OfflinePage />} />
          <Route path="/pwa-test" element={<PWATestPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <BackToHome />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#0d0d0f",
              border: "1px solid rgba(255,79,207,0.3)",
              color: "#fff",
            },
          }}
        />
        <OneSignalInit />
        <ServiceWorkerRegister />
        <HostQuietOverlays>
          <CookieBanner />
          <PWAInstallBanner />
          <IOSInstallBanner />
          <Suspense fallback={null}>
            <SubscribeLiveToasts />
            <ScratchGamePopup />
            <FaqBnbPopup />
          </Suspense>
        </HostQuietOverlays>
      </BrowserRouter>
    </AuthProvider>
  );
}

/**
 * ExternalRedirect — Hard client-side redirect to an external URL (es. sottodominio Hostland).
 * Lascia la SPA invece di renderizzare una pagina React, così non si duplica contenuto.
 */
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

/**
 * NotFoundPage — Vera pagina 404 (soft-404 lato HTTP, status 200 su hosting statico).
 * Nessun redirect automatico alla home: mostra il messaggio + link e imposta robots noindex.
 */
function NotFoundPage() {
  useEffect(() => {
    document.title = "Pagina non trovata · Vita da Host";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    meta.setAttribute("data-vdh-404", "1");
    document.head.appendChild(meta);
    return () => {
      const el = document.head.querySelector('meta[data-vdh-404="1"]');
      if (el) el.remove();
    };
  }, []);

  return (
    <div data-testid="not-found-page" className="min-h-screen bg-white text-neutral-900 flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div className="max-w-xl text-center">
          <p className="text-sm font-semibold tracking-widest text-neutral-400">ERRORE 404</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Pagina non trovata</h1>
          <p className="mt-4 text-base text-neutral-600">
            La pagina che cercavi non esiste o è stata spostata. Torna alla home per continuare a navigare.
          </p>
          <a
            href="/"
            data-testid="not-found-home-link"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-neutral-900 px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-black"
          >
            Torna alla home
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/**
 * HostQuietOverlays — Hides marketing overlays (ScratchGamePopup, SubscribeLiveToasts)
 * on host-facing internal routes like /devisu/*. Cookie banner + PWA install are
 * also suppressed there via a body class read by their own logic.
 */
function HostQuietOverlays({ children }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const isHostInternal = pathname.startsWith("/devisu/");
    document.body.classList.toggle("vdh-quiet-mode", isHostInternal);
    return () => document.body.classList.remove("vdh-quiet-mode");
  }, [pathname]);
  if (pathname.startsWith("/devisu/")) return null;
  return <>{children}</>;
}

export default App;
